import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck, Timer } from "lucide-react";
import { IMAGES } from "../constants";
import { useTilt } from "../hooks/useTilt";

const Hero = ({ onBookNow }) => {
    const tilt = useTilt(8);
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    useEffect(() => {
        const el = tilt.ref.current;
        if (el) el.style.transform = "rotateX(0deg) rotateY(0deg)";
    }, []);

    return (
        <section
            id="home"
            ref={sectionRef}
            className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16 sm:pt-28"
            style={{ background: "linear-gradient(160deg, #FFFFFF 0%, #F4F8FF 55%, #EBF2FF 100%)" }}
            data-testid="hero-section"
        >
            <div
                className="pointer-events-none absolute -top-32 -right-40 w-[36rem] h-[36rem] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(11,92,245,0.10) 0%, transparent 65%)" }}
            />
            <div
                className="pointer-events-none absolute bottom-0 -left-48 w-[30rem] h-[30rem] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(56,189,248,0.10) 0%, transparent 65%)" }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
                <div className="lg:col-span-6">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="inline-flex items-center gap-2 rounded-full border border-[#0B5CF5]/20 bg-white/80 backdrop-blur px-4 py-1.5 mb-7"
                        data-testid="hero-status-badge"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0B5CF5] opacity-60"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0B5CF5]"></span>
                        </span>
                        <span className="text-[11px] font-bold tracking-[0.22em] text-[#0B5CF5] uppercase">
                            Coming soon — Agartala
                        </span>
                    </motion.div>

                    <h1
                        className="font-display font-semibold text-slate-900 leading-[1.02] text-4xl sm:text-5xl lg:text-6xl"
                        style={{ letterSpacing: "-0.02em" }}
                        data-testid="hero-headline"
                    >
                        <span className="mask-line">
                            <span style={{ animationDelay: "0.25s" }}>Your Home.</span>
                        </span>
                        <span className="mask-line">
                            <span style={{ animationDelay: "0.42s" }}>Your Time.</span>
                        </span>
                        <span className="mask-line">
                            <span style={{ animationDelay: "0.59s" }} className="text-[#0B5CF5] italic font-medium">
                                Your Sevano.
                            </span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
                        data-testid="hero-subtext"
                    >
                        Reliable household help, whenever you need it. A simpler way to take care of everyday
                        chores is coming to Agartala.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-early-access-button"
                            onClick={onBookNow}
                            className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 text-sm sm:text-base"
                        >
                            Join the Waitlist <ArrowRight size={18} />
                        </button>
                        <a
                            href="#about"
                            data-testid="hero-discover-button"
                            onClick={(e) => {
                                e.preventDefault();
                                document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" });
                            }}
                            className="btn-ghost inline-flex items-center px-7 py-3.5 text-sm sm:text-base"
                        >
                            Discover Sevano
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500"
                    >
                        <span className="inline-flex items-center gap-2">
                            <ShieldCheck size={16} className="text-[#0B5CF5]" /> Verified helpers, planned
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <MapPin size={16} className="text-[#0B5CF5]" /> One locality at a time
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <Timer size={16} className="text-[#0B5CF5]" /> Honest arrival windows
                        </span>
                    </motion.div>
                </div>

                <div className="lg:col-span-6 tilt-scene">
                    <motion.div
                        initial={{ opacity: 0, y: 60, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        style={{ y: cardY }}
                        className="relative mx-auto max-w-lg lg:max-w-none"
                    >
                        <div
                            ref={tilt.ref}
                            onMouseMove={tilt.onMouseMove}
                            onMouseLeave={tilt.onMouseLeave}
                            className="tilt-card relative rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-24px_rgba(11,92,245,0.35)]"
                            data-testid="hero-3d-card"
                        >
                            <img
                                src="/hero-sofa.jpg"
                                alt="A bright, freshly cleaned living room — sofa with Sevano-blue cushion, cleaning caddy and a Sevano mug"
                                className="w-full h-[300px] sm:h-[420px] lg:h-[480px] object-cover"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#053BB8]/35 via-transparent to-transparent" />

                            <div className="absolute top-4 left-4 rounded-2xl bg-white/85 backdrop-blur-md px-4 py-3 shadow-lg animate-float">
                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Arrival target</p>
                                <p className="text-lg font-extrabold text-slate-900 leading-tight">
                                    30 min<span className="text-[#0B5CF5]">*</span>
                                </p>
                            </div>

                            <div className="absolute bottom-4 right-4 rounded-2xl bg-white/85 backdrop-blur-md px-4 py-3 shadow-lg animate-float-slow">
                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Launch zone</p>
                                <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                                    <MapPin size={14} className="text-[#0B5CF5]" /> Agartala, Tripura
                                </p>
                            </div>
                        </div>

                        <div
                            className="absolute -z-10 -inset-6 rounded-[2.5rem] opacity-60 blur-2xl"
                            style={{ background: "radial-gradient(circle at 30% 20%, rgba(11,92,245,0.25), transparent 60%)" }}
                        />

                        <p className="mt-4 text-xs text-slate-400 max-w-md" data-testid="hero-eta-disclaimer">
                            *The 30-minute arrival is an ambition we are working toward — it will depend on
                            launch-zone coverage and live helper availability, and we will always show honest
                            status before anything is confirmed.
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
